import React, { useState, useRef, useEffect } from 'react'
import styles from './speech.module.scss' // Reuse your existing styles

// Helper to map your 3-Letter codes (ISO 639-2/3) to Browser Speech API tags (BCP 47)
const getSpeechApiLocale = (code) => {
  // Ensure the incoming code is treated as lowercase for robust lookup
  const lowerCode = code ? code.toLowerCase() : ''; 

  const mapping = {
    // 3-Letter Codes (ISO) -> Speech API Tags (BCP 47)
    eng: 'en-US',
    rus: 'ru-RU',
    ita: 'it-IT',
    por: 'pt-BR', // Using Brazilian Portuguese locale
    tur: 'tr-TR',
    spa: 'es-ES',
    swe: 'sv-SE',
    nld: 'nl-NL',
    cmn: 'zh-CN', // Mandarin Chinese
    vie: 'vi-VN',
    kor: 'ko-KR',
    ara: 'ar-SA', // Arabic (Saudi Arabia/Standard)
    jpn: 'ja-JP',
    ell: 'el-GR', // Greek
    deu: 'de-DE',
    fra: 'fr-FR',
  }

  // Look up the code, default to US English if not found
  return mapping[lowerCode] || 'en-US' 
}

export default function SpeechPracticeRecorder({ onSpeechResult, spokenLang }) {
  const recognitionRef = useRef(null)
  const [isActive, setIsActive] = useState(false)
  const [isStoppedManually, setIsStoppedManually] = useState(false)

  // Initialize or Update Recognition when spokenLang changes
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition

    if (!SpeechRecognition) {
      console.error('SpeechRecognition API is not supported in this browser.')
      return
    }

    // Create instance
    const recognition = new SpeechRecognition()
    recognitionRef.current = recognition
    
    // Set the language based on the prop passed from parent (e.g., 'por' -> 'pt-BR')
    recognition.lang = getSpeechApiLocale(spokenLang)
    recognition.interimResults = false
    recognition.continuous = false // Usually better false for single phrase practice

    recognition.onstart = () => {
      setIsActive(true)
    }

    recognition.onspeechend = () => {
      // Automatically stop if the user stops talking
      if (!isStoppedManually) {
        recognition.stop()
      }
    }

    recognition.onend = () => {
      setIsActive(false)
      setIsStoppedManually(false)
    }

    recognition.onresult = (event) => {
      // Safely access the transcript
      const results = event.results
      if (results && results[0] && results[0][0]) {
        const transcript = event.results[0][0].transcript
        onSpeechResult(transcript)
      }
    }

    // Cleanup: Abort recognition if the component unmounts or language changes
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort()
      }
    }
  }, [spokenLang, onSpeechResult, isStoppedManually])

  const handleOnRecord = () => {
    if (isActive) {
      setIsStoppedManually(true)
      recognitionRef.current?.stop()
    } else {
      // Re-verify lang before starting just in case
      if (recognitionRef.current) {
        recognitionRef.current.lang = getSpeechApiLocale(spokenLang)
        try {
          recognitionRef.current.start()
        } catch (error) {
           console.warn("Speech recognition already started or error:", error)
        }
      }
    }
  }

  return (
    <div className={styles.languageSelector}>
      {/* Visual Feedback of what language is active */}
      <div style={{ marginBottom: '10px', fontSize: '0.9rem', color: '#666' }}>
        Recording in: <strong>{getSpeechApiLocale(spokenLang)}</strong>
      </div>

      <button 
        onClick={handleOnRecord}
        style={{
          backgroundColor: isActive ? '#d32f2f' : '#1976d2', // Red for stop, Blue for start
          color: 'white',
          padding: '10px 20px',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer'
        }}
      >
        {isActive ? 'Stop Recording' : 'Tap to Speak'}
      </button>
    </div>
  )
}